import axios from 'axios';
import { API_GATEWAY_URL, AUTH_SERVICE_URL } from '../config';
import refreshTokenManager from './refreshTokenManager';

// Shared manager координирует refresh между auth.js и apiService.js

// Отдельный экземпляр axios для авторизационных запросов
const authApi = axios.create({
  baseURL: API_GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Добавляем токен к авторизационным запросам
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');

  // Добавляем домен клиента в заголовки запроса
  const clientDomain = window.location.origin;
  config.headers['X-Client-Domain'] = clientDomain;

  // Добавляем токен, если он есть (валидация происходит на API Gateway)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Обработка ошибок 401 с автоматическим обновлением токена
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Если ошибка 401 и это не запрос на обновление токена и мы еще не пытались обновить токен
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh-token') &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/google') &&
      !originalRequest.url?.includes('/auth/logout')
    ) {
      originalRequest._retry = true;

      // Если уже идет обновление токена, используем существующий promise
      if (refreshTokenManager.isCurrentlyRefreshing()) {
        const existingPromise = refreshTokenManager.getRefreshPromise();
        if (existingPromise) {
          try {
            const token = await existingPromise;
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          } catch (err) {
            return Promise.reject(err);
          }
        }

        // Fallback: добавляем в очередь если promise не найден
        return new Promise((resolve, reject) => {
          refreshTokenManager.addToQueue(resolve, reject);
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      // Создаем shared promise для refresh операции
      const refreshPromise = (async () => {
        try {
          // Пытаемся обновить токен
          const response = await authApi.post('/auth/refresh-token');

          let newToken = null;
          if (response.data?.tokens?.accessToken) {
            newToken = response.data.tokens.accessToken;
            localStorage.setItem('token', newToken);
          } else if (response.data?.token) {
            newToken = response.data.token;
            localStorage.setItem('token', newToken);
          }

          // Обрабатываем очередь запросов
          refreshTokenManager.processQueue(null, newToken);
          return newToken;
        } catch (refreshError) {
          // Токен удаляем только если рефреш явно вернул 401
          // При сетевых ошибках, 5xx, таймауте — токен не трогаем
          if (refreshError?.response?.status === 401) {
            localStorage.removeItem('token');
          }
          refreshTokenManager.processQueue(refreshError);
          throw refreshError;
        } finally {
          refreshTokenManager.setRefreshing(false);
        }
      })();

      // Сохраняем promise и флаг ДО начала async операции,
      // чтобы параллельные запросы сразу получали существующий promise
      refreshTokenManager.setRefreshPromise(refreshPromise);
      refreshTokenManager.setRefreshing(true);

      try {
        const newToken = await refreshPromise;
        // Повторяем оригинальный запрос с новым токеном
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Экспортируем методы API для авторизации
export const authService = {
  // Вход в систему
  login: async (email, password) => {
    try {
      const response = await authApi.post('/auth/login', { email, password });

      if (response.data && response.data.tokens) {
        localStorage.setItem('token', response.data.tokens.accessToken);
      } else if (response.data && response.data.token) {
        // Поддержка старого формата ответа
        localStorage.setItem('token', response.data.token);
      }

      // NotificationsProvider смонтирован один раз на весь App (в т.ч. на /login,
      // ещё без токена) и не перезапускает свой fetch при клиентской навигации —
      // без этого события счётчик уведомлений повисает пустым до F5
      window.dispatchEvent(new Event('auth:login'));

      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Google OAuth login
  loginWithGoogle: () => {
    const redirectUri = `${window.location.origin}/oauth-callback`;
    const googleAuthUrl = `${AUTH_SERVICE_URL}/api/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = googleAuthUrl;
  },

  // Exchange access token for refresh token cookie
  exchangeToken: async () => {
    try {
      const response = await authApi.post('/auth/exchange-token');

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Handle OAuth callback
  handleOAuthCallback: async (accessToken) => {
    try {
      // Store access token FIRST (interceptor will use it)
      localStorage.setItem('token', accessToken);
      window.dispatchEvent(new Event('auth:login'));

      // Exchange access token for refresh token cookie
      await authService.exchangeToken();

      return { success: true };
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  },

  // Обновление токена
  refreshToken: async () => {
    try {
      const response = await authApi.post('/auth/refresh-token');

      if (response.data && response.data.tokens) {
        localStorage.setItem('token', response.data.tokens.accessToken);
      } else if (response.data && response.data.token) {
        // Поддержка старого формата ответа
        localStorage.setItem('token', response.data.token);
      }

      return response.data;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Токен удаляем только если рефреш явно вернул 401
      // При сетевых ошибках, 5xx, таймауте — токен не трогаем
      if (error?.response?.status === 401) {
        localStorage.removeItem('token');
      }
      throw error;
    }
  },

  // Проверка валидности токена
  validateToken: async () => {
    try {
      const response = await authApi.get('/auth/validate-token');
      return response.data;
    } catch (error) {
      console.error('Token validation error:', error);
      return {
        valid: false,
        message: error.response?.data?.message || 'Error validating token',
      };
    }
  },

  // Выход из системы
  logout: async () => {
    try {
      await authApi.post('/auth/logout');
      localStorage.removeItem('token');
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Все равно очищаем локальное хранилище
      localStorage.removeItem('token');
      throw error;
    }
  },

  // Получение текущего пользователя
  getCurrentUser: async () => {
    try {
      const response = await authApi.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },

  // Обновление профиля пользователя
  updateProfile: async (profileData) => {
    try {
      const response = await authApi.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  },

  // Изменение пароля
  changePassword: async (passwordData) => {
    try {
      const response = await authApi.post('/auth/change-password', passwordData);
      return response.data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  },

  // Получение токена из localStorage
  getToken: () => {
    return localStorage.getItem('token');
  },

  // Сохранение токена в localStorage
  setToken: (token) => {
    localStorage.setItem('token', token);
  },

  // Удаление токена из localStorage
  removeToken: () => {
    localStorage.removeItem('token');
  },
};

export default authService;